import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

import "./DropdownMenu.css";

const GAP = 8;
const EDGE = 12;

const renderIcon = (icon) => {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;

  if (
    typeof icon === "function" ||
    (typeof icon === "object" && icon.$$typeof)
  ) {
    return React.createElement(icon, {
      size: 17,
      "aria-hidden": true,
    });
  }

  return null;
};

export default function DropdownMenu({
  items = [],
  trigger,
  label = "Open menu",
  align = "end",
  placement = "bottom",
  width = 200,
  disabled = false,
  className = "",
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const closeMenu = (restoreFocus = true) => {
    setOpen(false);
    setPosition(null);
    onOpenChange?.(false);

    if (restoreFocus) {
      triggerRef.current?.querySelector("button")?.focus();
    }
  };

  const toggleMenu = () => {
    if (disabled) return;

    if (open) {
      closeMenu();
    } else {
      setPosition(null);
      setOpen(true);
      onOpenChange?.(true);
    }
  };

  const updatePosition = () => {
    const triggerElement = triggerRef.current;
    const menuElement = menuRef.current;

    if (!triggerElement || !menuElement) return;

    const rect = triggerElement.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const menuWidth = Math.max(0, Math.min(width, viewportWidth - EDGE * 2));

    const menuHeight = menuElement.scrollHeight;

    const below = Math.max(0, viewportHeight - rect.bottom - GAP - EDGE);

    const above = Math.max(0, rect.top - GAP - EDGE);

    const openAbove =
      placement === "top"
        ? above >= menuHeight || above > below
        : below < menuHeight && above > below;

    const availableHeight = openAbove ? above : below;
    const maxHeight = Math.min(360, availableHeight);

    const left = Math.max(
      EDGE,
      Math.min(
        align === "start" ? rect.left : rect.right - menuWidth,
        viewportWidth - menuWidth - EDGE,
      ),
    );

    const top = openAbove
      ? Math.max(EDGE, rect.top - GAP - Math.min(menuHeight, maxHeight))
      : rect.bottom + GAP;

    setPosition({
      top,
      left,
      width: menuWidth,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;

    updatePosition();
  }, [open, width, align, placement, items.length]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      closeMenu(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key === "Tab") {
        closeMenu(false);
        return;
      }

      if (
        event.key !== "ArrowDown" &&
        event.key !== "ArrowUp" &&
        event.key !== "Home" &&
        event.key !== "End"
      ) {
        return;
      }

      const buttons = Array.from(
        menuRef.current?.querySelectorAll(
          ".dropdown-menu__item:not(:disabled)",
        ) || [],
      );

      if (!buttons.length) return;

      event.preventDefault();

      const currentIndex = buttons.indexOf(document.activeElement);

      let nextIndex;

      if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = buttons.length - 1;
      } else {
        const direction = event.key === "ArrowDown" ? 1 : -1;

        nextIndex =
          (currentIndex + direction + buttons.length) % buttons.length;
      }

      buttons[nextIndex]?.focus();
    };

    document.addEventListener("pointerdown", handleOutside);
    document.addEventListener("keydown", handleKeyDown);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("pointerdown", handleOutside);
      document.removeEventListener("keydown", handleKeyDown);

      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, align, placement, width]);

  useEffect(() => {
    if (!open || !position) return;

    const frame = requestAnimationFrame(() => {
      menuRef.current
        ?.querySelector(".dropdown-menu__item:not(:disabled)")
        ?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [open, Boolean(position)]);

  useEffect(() => {
    if (disabled && open) {
      closeMenu(false);
    }
  }, [disabled, open]);

  const handleItemClick = (item) => {
    if (item.disabled) return;

    closeMenu(false);
    item.onClick?.(item);
  };

  const triggerProps = {
    onClick: toggleMenu,
    expanded: open,
    disabled,
  };

  const triggerContent =
    typeof trigger === "function" ? (
      trigger(triggerProps)
    ) : React.isValidElement(trigger) ? (
      React.cloneElement(trigger, {
        onClick: (event) => {
          trigger.props.onClick?.(event);
          if (!event.defaultPrevented) toggleMenu();
        },
      })
    ) : (
      <button
        type="button"
        className="dropdown-menu__default-trigger"
        onClick={toggleMenu}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        {label}
        <ChevronDown size={16} />
      </button>
    );

  return (
    <>
      <div
        ref={triggerRef}
        className={`dropdown-menu__trigger ${className}`.trim()}
      >
        {triggerContent}
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            className="dropdown-menu__popup"
            style={
              position
                ? {
                    top: position.top,
                    left: position.left,
                    width: position.width,
                    maxHeight: position.maxHeight,
                    visibility: "visible",
                  }
                : {
                    top: 0,
                    left: 0,
                    width,
                    visibility: "hidden",
                  }
            }
          >
            {items.map((item, index) => {
              if (item.divider) {
                return (
                  <div
                    key={item.id || `divider-${index}`}
                    className="dropdown-menu__divider"
                    role="separator"
                  />
                );
              }

              return (
                <button
                  key={item.id ?? index}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  className={[
                    "dropdown-menu__item",
                    item.active && "dropdown-menu__item--active",
                    item.danger && "dropdown-menu__item--danger",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleItemClick(item)}
                >
                  {item.icon && (
                    <span className="dropdown-menu__icon">
                      {renderIcon(item.icon)}
                    </span>
                  )}

                  <span className="dropdown-menu__label">{item.label}</span>

                  {item.description && (
                    <span className="dropdown-menu__description">
                      {item.description}
                    </span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
